// MedHive site content — sourced from medhive.ai extraction (content unchanged)
// Team/Advisors/Investors updated per fundraise alignment brief

export const BRAND = {
  name: "MedHive",
  logo: "/assets/medhive_assets/logos/medhive.e9d5254a.png",
  logoMark: "/assets/medhive_assets/logos/logo.2a4581c0.png",
  email: "contact.us@medhive.ai",
  location: "Atlanta, GA 30307",
  linkedin: "https://www.linkedin.com/company/medhive-ai",
  x: "https://x.com/MedhiveAI",
};

export const BOOK_DEMO_URL =
  "https://meetings.hubspot.com/vipul-kashyap-crm/medhive-prospect?uuid=7cd4d43a-a9b3-489e-adac-bc7a27fe9ed3";

export const NAV_LINKS = [
  { label: "home", to: "/" },
  { label: "company", to: "/company" },
  { label: "platform", to: "/platform" },
  { label: "security", to: "/security" },
  { label: "blogs", to: "/blogs" },
  { label: "contact us", to: "/contact-us" },
];

export const HERO = {
  heading: "REVOLUTIONIZING THE MEDICAL DEVICE DEVELOPMENT",
  body: "One design change ripples across requirements, risk files, DHF/DMR, labeling, test protocols and suppliers — and teams lose weeks tracing it by hand. MedHive maps the full impact of a change across your systems. Built with medical-device R&D, quality and regulatory leaders.",
  cta: "join the design-partner programme",
  ctaSecondary: "book a walkthrough",
};

export const CHANGE_IMPACT = {
  eyebrow: "how it works",
  heading: "Cross-system change impact for sustaining engineering",
  body: "MedHive builds a knowledge graph of your product documentation, so a proposed change can be traced to every artifact it touches — before the change package is opened. Instead of hand-searching six systems, your team sees the full blast radius of a change in one view, with traceability back to the source documents.",
  systems: [
    { icon: "ListChecks", label: "Requirements" },
    { icon: "ShieldAlert", label: "Risk Files" },
    { icon: "FolderArchive", label: "DHF / DMR" },
    { icon: "Tag", label: "Labeling" },
    { icon: "ClipboardList", label: "Test Protocols" },
    { icon: "Truck", label: "Suppliers" },
  ],
};

export const ABOUT = {
  eyebrow: "about medhive",
  heading: "welcome to MEDHIVE",
  body: "MedHive is an AI-based Knowledge Management Platform specifically developed for the medical device industry to transform the R&D processes, eliminate data barriers, and open a quicker, more efficient pathway from concept to market.",
  targetsLabel: "What we measure with every design partner",
  targetsCaption: "Baselined together at kickoff — results shared only with partner approval",
  stats: [
    { value: "Prep Time", label: "Change-package preparation time, baselined with your team in week one and tracked through the pilot." },
    { value: "Traceability", label: "Systems traced per change — requirements, risk files, DHF/DMR, labeling and test protocols." },
    { value: "Pilot Scope", label: "Duration and success criteria agreed together before kickoff — no open-ended engagements." },
  ],
};

export const PLATFORM = {
  eyebrow: "platform overview",
  heading: "How MEDHIVE Works",
  body: "MedHive unifies fragmented knowledge, automates repetitive tasks, and empowers your cross-functional teams with the insights needed to accelerate innovation and improve decision-making — designed to shorten time-to-market, and we will measure it with you.",
  cards: [
    {
      icon: "FlaskConical",
      title: "Accelerated Medical Device R&D",
      body: "MedHive unifies fragmented knowledge, automates repetitive tasks, and empowers your cross-functional teams to accelerate innovation.",
    },
    {
      icon: "FileCheck2",
      title: "Regulatory Document Creation",
      body: "Proactively generates regulatory drafts and automates compliance documentation to keep submissions on schedule.",
    },
    {
      icon: "Network",
      title: "Knowledge Collaboration Hub",
      body: "A single intelligent knowledge graph that connects Product, Quality, Operations, and Finance teams.",
    },
  ],
};

export const USE_CASES = {
  eyebrow: "use cases",
  heading: "Making a Big Impact on People's Lives",
  body1: "By reducing the time spent tracing changes and assembling documentation, MedHive is designed to help life-saving medical devices—from advanced implants to vital diagnostic tools—move from concept to patient bedside sooner.",
  body2: "MedHive is designed to help medical device manufacturers improve product reliability and documentation quality, supporting better patient outcomes. We will measure these outcomes together with each design partner.",
  cta: "learn more",
};

export const SECURITY = {
  eyebrow: "security",
  heading: "Committed to Security",
  body: "MedHive's AI architecture is designed to follow the major data privacy and security regulations (including HIPAA and GDPR principles), offering options for secure cloud deployment and stringent Role-Based Access Controls (RBAC) to protect your intellectual property and sensitive data.",
  points: [
    "Data encrypted in transit (TLS 1.2+) and at rest",
    "Role-based access control (RBAC) and least-privilege access",
    "Continuous monitoring, logging and alerting",
    "SOC 2 Type I audit planned — timeline available on request; controls designed to ISO 27001",
  ],
};

export const WHY_US = {
  eyebrow: "why choose us",
  heading: "Why MEDHIVE?",
  body: "MedHive significantly enhances core QMS/PLM capabilities by providing a Unified Knowledge View across all vital business domains—Product, Quality, Operations, and Finance. With a powerful Neuro-Symbolic AI approach, leveraging both historical and current knowledge to generate concrete outputs, MedHive streamlines development and compliance.",
  competitors: ["MedHive", "LLMs (chat, API)", "Ketryx", "Collate", "Siemens Teamcenter", "PTC Windchill", "Greenlight Guru", "Qualio"],
  // f = full, p = partial, n = none; strings for text rows
  matrix: [
    { feature: "Content & traceability MindMap", vals: ["f", "n", "n", "n", "n", "n", "n", "n"] },
    { feature: "Domain / context-targeted AI", vals: ["f", "p", "p", "p", "p", "p", "p", "p"] },
    { feature: "Historical knowledge reuse", vals: ["f", "n", "p", "p", "n", "n", "n", "n"] },
    { feature: "Per-artifact accuracy score (RF)", vals: ["f", "n", "n", "n", "n", "n", "n", "n"] },
    { feature: "Automated artifact generation", vals: ["f", "f", "f", "f", "p", "n", "n", "p"] },
    { feature: "Automated change management", vals: ["f", "n", "p", "f", "n", "n", "n", "n"] },
    { feature: "Gaps identification", vals: ["f", "n", "p", "n", "n", "n", "n", "p"] },
    { feature: "Cost", vals: ["Low", "Low", "High", "High", "High", "High", "High", "High"] },
  ],
  footnotes: [
    "An AI-first approach that leverages historical information plus domain- and workflow-specific context — with per-artifact RF scoring to catch inaccurate AI-generated knowledge.",
    "Suite agents draft only inside their own system of record — an agent cannot walk a dependency graph it doesn't have.",
  ],
};

export const COMPANY = {
  aboutEyebrow: "about medhive",
  aboutHeading: "Driving the future of medical device innovation",
  aboutBody1: "MedHive was founded on the principle that fragmented data should never hinder life-saving innovation. We empower medical device manufacturers to unify their technical, quality, regulatory, and operational data into a single, intelligent knowledge graph. Our unique Neuro-Symbolic AI transforms scattered information into actionable insights, proactively generates regulatory drafts, and suggests critical design optimizations.",
  aboutBody2: "Our mission is to significantly reduce time-to-market, minimize compliance risk, and unleash the full creative potential of R&D teams globally.",
  teamEyebrow: "executive",
  teamHeading: "THE LEADERSHIP TEAM",
  teamBody: "Our leadership team brings together decades of experience in artificial intelligence, medical device engineering, and global regulatory affairs. They are united by a vision to make complex medical device development faster, safer, and more reliable through cutting-edge technology.",
  careersEyebrow: "careers",
  careersHeading: "JOIN OUR TEAM",
  careersBody: "Join our team of innovators who are revolutionizing how medical devices are brought to market.",
  careersCta: "Current Open Positions",
};

export const DESIGN_PARTNERS = {
  eyebrow: "design partners",
  heading: "Now selecting design partners",
  body: "We are working hands-on with a small number of sustaining engineering, R&D and QA/RA teams at medical-device companies. Partners get early access to the platform, direct input into the roadmap, and hands-on onboarding from our team. In return, we ask for a real change scenario from your product line, regular feedback sessions, and permission to measure the outcomes together.",
  bullets: [
    "Eligibility: sustaining engineering, R&D or QA/RA teams at medical-device companies",
    "Duration: a fixed-length pilot, scoped together with your team before kickoff",
    "Availability: a limited number of partner slots — we work closely with each team",
    "What we measure together: change-package prep time, systems traced per change, documentation effort",
  ],
  cta: "Apply to be a design partner",
};

// Fundraise-aligned team structure (canonical order — use across all assets)
export const MANAGEMENT_TEAM = [
  {
    name: "Vipul Kashyap",
    experience: "",
    title: "Co-founder & CEO",
    photo: "/assets/team/vipul.webp",
    linkedin: "https://www.linkedin.com/in/vipulkashyap/",
  },
  {
    name: "Vivek Dixit",
    experience: "",
    title: "Co-founder & COO",
    photo: "/assets/team/vivek.webp",
    linkedin: "https://www.linkedin.com/in/vivekdixit/",
  },
  {
    name: "Jason Glithero",
    experience: "",
    title: "Product Leader",
    photo: "/assets/team/jason.webp",
    linkedin: "https://www.linkedin.com/in/jasonglithero/",
  },
  {
    name: "Murali Tammineni",
    experience: "",
    title: "Tech Lead",
    photo: "/assets/team/murali.webp",
    linkedin: "https://www.linkedin.com/in/muralitammineni/",
  },
  {
    name: "Christopher Kaswer",
    experience: "",
    title: "Product Lead",
    photo: "/assets/team/christopher.webp",
    linkedin: "https://www.linkedin.com/in/christopher-kaswer-60a5582/",
  },
  {
    name: "Rina Kacker",
    experience: "",
    title: "FP&A Lead",
    photo: "/assets/team/rina.webp",
    linkedin: "https://www.linkedin.com/in/rina-kacker/",
  },
];

export const ADVISORS = [
  {
    name: "Shalabh Chandra",
    experience: "",
    title: "Med-Device Executive",
    photo: "/assets/team/shalabh.webp",
    linkedin: "https://www.linkedin.com/in/shalabhchandra/",
  },
  {
    name: "David Scampoli",
    experience: "",
    title: "MedTech SME",
    photo: "/assets/team/david.webp",
    linkedin: "https://www.linkedin.com/in/david-scampoli/",
  },
  {
    name: "Ganapathy Pichumani",
    experience: "",
    title: "Tech & AI",
    photo: "/assets/team/ganapathy.webp",
    linkedin: "https://www.linkedin.com/in/ganapathy-pichumani/",
  },
  {
    name: "Badri Ramkrishnan",
    experience: "",
    title: "Growth",
    photo: "/assets/team/badri.webp",
    linkedin: "https://www.linkedin.com/in/badriramki/",
  },
];

export const CONTACT = {
  heading: "Leave a Message",
  body: "Tell us about your change-management scenario or apply to the design-partner programme. We read every enquiry and reply as quickly as we can.",
  consent: "I agree to the MedHive Privacy Policy",
  button: "Submit",
};

export const FOOTER = {
  legal: "Medhive is a Delaware incorporated medical devices technology, AI and SaaS company. Use of this site and platform is subject to our Terms of Use and Privacy Policy. Medhive does not provide medical advice, diagnosis, or treatment.",
  quickLinks: [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms of Use", to: "/terms" },
    { label: "Blogs", to: "/blogs" },
    { label: "Contact Us", to: "/contact-us" },
  ],
  copyright: "\u00A9 Medhive, Inc. All rights reserved.",
};

// ============ PLATFORM PAGE ============
export const PLATFORM_PAGE = {
  heading: "MEDHIVE'S AI-POWERED SOLUTIONS",
  sub: "Give your R&D team a competitive advantage with MedHive.",
  body: "MedHive offers three seamlessly integrated solutions—each engineered to accelerate your product lifecycle, unify your knowledge base, and empower your cross-functional teams with actionable, intelligent insights. MedHive allows you to allocate critical resources to pioneering solutions rather than repetitive administrative and research tasks.",
  checks: ["Ensure Speed", "Compliance", "Accurate Data"],
  solutions: [
    {
      id: "sol1",
      eyebrow: "SOLUTION 01",
      title: "ACCELERATED MEDICAL DEVICE R&D",
      body: "Change how your engineering and design teams operate by cutting the time spent on searching, analyzing, and verifying information — a significant share of the development cycle. By leveraging advanced data analytics and machine learning algorithms of MedHive, your team can get instantaneous answers and predictive modeling capabilities, guiding designers to the most optimal, lowest-risk pathways from the initial concept phase.",
      points: [
        { icon: "BarChart3", text: "Analyzes historical project data" },
        { icon: "GitCompare", text: "Compares failed tests and successful designs" },
        { icon: "TrendingUp", text: "Identifies trends automatically" },
        { icon: "Lightbulb", text: "Suggests new design/ component iterations" },
        { icon: "ShieldCheck", text: "Enhances safety and performance" },
      ],
    },
    {
      id: "sol2",
      eyebrow: "SOLUTION 02",
      title: "REGULATORY DOCUMENT CREATION",
      body: "Automatically ingest your current design inputs and verification reports to generate high-fidelity, comprehensive draft regulatory submissions - such as initial 510(k) sections or CE Mark technical documentation. MedHive utilizes cutting-edge Neuro-Symbolic AI to transition compliance from a manual burden to an automated asset, while also performing real-time gap analysis and flagging missed design suggestions required for compliance.",
      points: [
        { icon: "BrainCircuit", text: "Uses the statistical power of machine learning" },
        { icon: "Workflow", text: "Offers structured and rule-based logic" },
        { icon: "Globe", text: "Follows global medical device standards" },
        { icon: "Database", text: "Leverages your organization's QMS data" },
        { icon: "BadgeCheck", text: "Ensures regulatory adherence from day one" },
      ],
    },
    {
      id: "sol3",
      eyebrow: "SOLUTION 03",
      title: "KNOWLEDGE COLLABORATION HUB",
      body: "Break down the barriers between cross-functional teams and allow engineers, regulatory experts, and product managers to access and share contextually relevant data instantly from a single source. The Knowledge Collaboration Hub acts as the central nervous system of your R&D ecosystem, offering a Unified Knowledge View across historically siloed domains, including Product (PLM), Quality (QMS), Operations, and Finance.",
      points: [
        { icon: "Share2", text: "Links all components, risks, clinical data, and financial forecasts" },
        { icon: "FileSearch", text: "Ensures that every decision is fully informed and traceable" },
        { icon: "Users", text: "Brings all the stakeholders on one platform" },
        { icon: "Sparkles", text: "Fosters a dynamic and inclusive innovation cycle" },
        { icon: "Rocket", text: "Accelerates the development of superior quality products" },
      ],
    },
  ],
  faqEyebrow: "good to know",
  faqHeading: "FREQUENTLY ASKED QUESTIONS",
  faqBody: "Got questions about MedHive's technology, implementation, security, and pricing?",
  faqs: [
    {
      q: "What is MedHive?",
      a: "MedHive is a neuro-symbolic AI platform that unifies all knowledge from the medical device lifecycle. It understands legacy documentation, retrieves insights in seconds, and generates accurate drafts of artifacts such as reports or 510k submissions. MedHive helps teams converge on better designs, accelerate development, and improve predictability across the entire R&D process.",
    },
    {
      q: "How is MedHive different from a QMS or PLM system?",
      a: "QMS and PLM tools manage documents, workflows, and compliance steps. They do not interpret knowledge, reveal insights, generate content, or support early-phase engineering work. MedHive captures, understands, and connects every piece of device knowledge from incubation through sustainment. This includes exploration, requirements, concept development, verification, regulatory documentation, and field feedback. It then uses neuro-symbolic AI to guide design decisions and generate content that normally requires extensive manual effort.",
    },
    {
      q: "How does MedHive reduce R&D time and cost?",
      a: "MedHive retrieves past insights within seconds, generates high-quality drafts of technical and regulatory documents, and highlights potential gaps. It eliminates repeated work, shortens discovery and onboarding time for new hires as well as seasoned subject matter experts, and reduces time spent searching through scattered files. Evidence from early case studies shows savings across design, testing, documentation, and regulatory preparation.",
    },
    {
      q: "Does MedHive help prepare FDA submissions?",
      a: "Yes. MedHive can generate draft submissions, such as 510k, based on the knowledge you provide. It analyzes documentation from the entire device lifecycle, aligns content with regulatory structure, and produces a draft that your regulatory team can finalize. This process normally requires weeks or months of manual assembly.",
    },
    {
      q: "How does MedHive prevent hallucinations?",
      a: "MedHive uses a domain-specific knowledge graph combined with a small language model that only answers questions based on verified content within your secure environment. Every answer includes traceability back to the specific documents or knowledge sources used. We also generate a \u201Challucination score\u201D that identifies the likelihood of the responses having hallucinations. This prevents unsupported reasoning, alerts the user to possible hallucinations, and ensures that all generated content can be validated.",
    },
    {
      q: "What types of content does MedHive ingest?",
      a: "MedHive ingests market research, requirements, design files, specifications, protocols, reports, memos, presentations, onboarding materials, regulatory documentation, field data, patents, and more. Internal and external content can be combined into a unified knowledge graph that becomes the foundation for retrieval and generation.",
    },
    {
      q: "How quickly can MedHive retrieve knowledge?",
      a: "Search and comprehension take seconds, even for large bodies of content. Engineers and product managers can ask targeted questions, review structured knowledge, and follow traceability links to original documents with ease.",
    },
    {
      q: "How does MedHive safeguard our internal data and ensure cybersecurity?",
      a: "Your internal data is subject to strict environmental isolation and security controls. MedHive operates entirely within your protected boundary, leveraging techniques like semantic embeddings and knowledge graphs only inside this secure environment. Your proprietary content is never used to train external models, cannot leave your environment, and remains completely inaccessible to any other customer. This architectural design ensures absolute data sovereignty and confidentiality.",
    },
    {
      q: "What does onboarding look like?",
      a: "Onboarding includes ingestion of representative device documentation, knowledge graph setup, user training, and demonstration of value exercises. Most customers see meaningful results within the first few weeks. A dedicated MedHive team guides configuration and handles ingestion support during initial deployment.",
    },
    {
      q: "What integrations does MedHive support?",
      a: "MedHive integrates with common document repositories, QMS systems, PLM platforms, cloud storage, and internal shared drives. It can ingest structured exports from design and quality systems. API based integration options are available for custom enterprise environments.",
    },
    {
      q: "How does MedHive support compliance and audits?",
      a: "MedHive identifies compliance gaps by comparing content against expectations for quality and regulatory documentation. It provides traceability to source knowledge and organizes artifacts in a way that makes audit preparation more predictable.",
    },
    {
      q: "Does MedHive support international teams and distributed R&D?",
      a: "Yes. MedHive is cloud-based and supports global collaboration. Content access is controlled through permission settings, and activity is monitored to protect sensitive data.",
    },
    {
      q: "What customer support does MedHive provide?",
      a: "Each customer receives access to a dedicated support team that provides guidance, ingestion support, and help with best practices. Priority support options are available for enterprise deployments.",
    },
    {
      q: "What results can we expect in the first ninety days?",
      a: "Teams typically experience measurable improvements in knowledge retrieval time, significant reduction in the product development process, onboarding speed, regulatory preparation, and decision clarity. In some cases, MedHive has enabled teams to locate key insights or images across large patent sets within minutes. Customers often see an immediate reduction in time spent performing manual research.",
    },
  ],
};

// ============ SECURITY PAGE ============
export const SECURITY_PAGE = {
  heading: "MEDHIVE'S COMMITMENT TO SECURITY",
  body: "MedHive has a robust security and compliance program, designed to scale with the platform and meet the expectations of our partners, innovators, and enterprise teams. Customer operational data is not just a responsibility\u2014it's a core part of how we build trust.",
  groups: [
    {
      eyebrow: "SECURITY & COMPLIANCE",
      title: "STRONG BASELINE SECURITY",
      items: [
        { icon: "Lock", title: "Encryption in Transit", text: "Every interaction\u2014APIs, dashboard sessions, and provider endpoints\u2014is secured with TLS 1.2+, ensuring data stays protected from point to point." },
        { icon: "KeyRound", title: "Secure Credential Practices", text: "Passwords are never stored in plaintext. We use modern, secure hashing algorithms to safeguard user authentication at all times." },
        { icon: "HardDrive", title: "Encryption at Rest", text: "All MedHive data is encrypted using modern AES-256 or equivalent industry-standard encryption." },
        { icon: "Layers", title: "Protected at All Levels", text: "System built on a security-first foundation that protects sensitive health data at every layer." },
      ],
    },
    {
      eyebrow: "SECURITY & COMPLIANCE",
      title: "PLATFORM-LEVEL SAFEGUARDS",
      items: [
        { icon: "Fingerprint", title: "Multi-Factor Authentication (MFA)", text: "Required for administrative and partner access to strengthen account security." },
        { icon: "KeySquare", title: "Scoped API Keys", text: "Granular API permissions with automated rotation to reduce risk and simplify security hygiene." },
        { icon: "UserCog", title: "Role-Based Access Controls (RBAC)", text: "Fine-grained permissions tailored for clinical, operational, and administrative workflows." },
        { icon: "Globe", title: "IP Allow-Listing", text: "Optional access restriction for enterprise partners seeking an additional layer of network-level control (currently rolling out)." },
      ],
    },
    {
      eyebrow: "SECURITY & COMPLIANCE",
      title: "OPERATIONAL SECURITY IN MOTION",
      items: [
        { icon: "Radar", title: "Continuous Infrastructure Monitoring", text: "Real-time observability and anomaly detection across core systems." },
        { icon: "RefreshCw", title: "Automated Patching", text: "Critical systems and services are updated promptly, minimizing exposure windows." },
        { icon: "ScanSearch", title: "Vulnerability & Dependency Scanning", text: "Routine checks to identify and remediate risks before they impact the platform." },
        { icon: "Boxes", title: "Environment Isolation", text: "Staging, development, and production environments remain strictly separated to prevent cross-contamination and maintain system integrity." },
      ],
    },
  ],
  compliance: {
    title: "Compliance & Data Security Measures",
    body: "MedHive is actively working toward leading healthcare-ready security and compliance frameworks. While formal certifications are underway, the following controls and practices are already in operation.",
    items: [
      "Industry-aligned security controls",
      "Controls designed to ISO/IEC 27001 (formal certification underway)",
      "SOC 2 Type I audit planned — timeline available on request",
      "CIS-based infrastructure hardening",
      "SAST/DAST integrated into CI/CD pipelines",
      "Annual independent penetration testing (program expanding as we scale)",
      "Software Composition Analysis (SCA) for package and dependency security",
      "GDPR-aligned data minimization and privacy workflows",
      "Standardized Data Processing Addendum (DPA)\u2014available upon request",
      "Formalized data retention & deletion schedules (deployment in progress)",
    ],
  },
  governance: {
    title: "Commitment to Data Governance",
    sections: [
      {
        title: "Regulatory Alignment",
        body: "MedHive is actively working toward leading healthcare-ready security and compliance frameworks. While formal certifications are underway, the following practices are already in operation.",
        items: [
          "GDPR compliance alignment (EU)",
          "CCPA/CPRA alignment for consumer privacy (US)",
          "Adherence to customer contracts, DPAs, and responsible data use policies",
        ],
      },
      {
        title: "Data Governance Practices",
        body: "We treat customer data with the highest level of stewardship and transparency. You own your data \u2014 MedHive processes data solely on your behalf.",
        items: [
          "Comprehensive audit trails for core system actions",
          "Evolving data residency options for geographic and regulatory needs",
          "Ongoing reviews of data minimization and purpose-limitation principles",
        ],
      },
      {
        title: "Internal Governance",
        body: "Strong internal oversight ensures that our people and processes maintain consistent security discipline.",
        items: [
          "Security policies reviewed and maintained by leadership",
          "Access to production systems restricted and periodically reviewed",
          "Security awareness built into engineering onboarding and ways of working",
        ],
      },
    ],
  },
  cloud: {
    title: "Secure, Hardened, Cloud-Native Environment",
    body: "MedHive operates on secure, modern cloud platforms (AWS/Azure/GCP) designed for resilience and isolation.",
    items: [
      "Multi-zone redundancy for resilience",
      "Private VPC networking and segmented subnets",
      "Cloud-native Web Application Firewall (WAF) protection",
      "Built-in DDoS mitigation",
      "High-availability and failover enhancements (actively being expanded)",
      "Application-Level Protections",
      "Secure session management with short-lived tokens",
      "Continuous monitoring for suspicious behavior",
      "Business Continuity & Recovery",
      "Daily encrypted backups",
      "Documented disaster recovery workflows (formal DR program in progress)",
      "RTO/ RPO definitions are being finalized to support future enterprise deployments",
    ],
  },
  roadmapEyebrow: "compliance roadmap",
  roadmapHeading: "COMPLIANCE ROADMAP",
  roadmap: [
    { title: "SOC 2 Type I", note: "Audit planned — timeline available on request" },
    { title: "ISO/IEC 27001", note: "Controls designed to the standard; formal certification underway" },
    { title: "Security questionnaire", note: "Reach out via our contact page to request our security questionnaire" },
  ],
};

// ============ BLOGS PAGE ============
export const BLOGS_PAGE = {
  eyebrow: "insights",
  heading: "BLOGS",
  body: "Insights on AI, regulatory compliance, and the future of medical device R&D.",
  posts: [
    {
      date: "2/4/2026",
      title: "Accelerating medical device R&D with AI",
      thumb: "/assets/blog-thumb.png",
      url: "https://www.medhive.ai/blogs/accelerating-medical-device-r-d-with-ai",
    },
  ],
};

// Mock: store contact submissions locally until backend is connected
export const saveContactSubmission = (data) => {
  const existing = JSON.parse(localStorage.getItem("medhive_contact_submissions") || "[]");
  existing.push({ ...data, id: Date.now(), submittedAt: new Date().toISOString() });
  localStorage.setItem("medhive_contact_submissions", JSON.stringify(existing));
  return true;
};
